import { 
  Type, 
  Unit, 
  CostCenter, 
  Category, 
  ExpenseDataFilters,
  HIERARCHY_VALIDATION_RULES 
} from '../types/dataHierarchy';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validation utilities for data hierarchy relationships
 * Ensures meaningful comparisons and prevents invalid data combinations
 */
export class DataHierarchyValidator {
  
  /**
   * Validate expense data filters against hierarchy rules
   */
  static validateExpenseFilters(
    filters: ExpenseDataFilters,
    units: Unit[],
    costCenters: CostCenter[]
  ): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate units belong to the specified type
    const selectedUnits = units.filter(unit => filters.unitIds.includes(unit.id));
    const invalidUnits = selectedUnits.filter(unit => unit.typeId !== filters.typeId);
    
    if (invalidUnits.length > 0) {
      errors.push(`Units ${invalidUnits.map(u => u.name).join(', ')} do not belong to the selected type`);
    }

    // Validate cost centers belong to the specified type
    const selectedCostCenters = costCenters.filter(cc => filters.costCenterIds.includes(cc.id));
    const invalidCostCenters = selectedCostCenters.filter(cc => cc.typeId !== filters.typeId);
    
    if (invalidCostCenters.length > 0) {
      errors.push(`Cost centers ${invalidCostCenters.map(cc => cc.name).join(', ')} do not belong to the selected type`);
    }

    // Check for meaningful comparisons
    if (selectedUnits.length > 1) {
      const uniqueTypes = [...new Set(selectedUnits.map(u => u.typeId))];
      if (uniqueTypes.length > 1) {
        errors.push('Cannot compare units from different business types');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Validate individual form selections for expense entry
   */
  static validateFormSelection(
    typeId: string,
    unitId: string,
    costCenterId: string,
    categoryId: string,
    units: Unit[],
    costCenters: CostCenter[],
    categories: Category[]
  ): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!typeId) {
      errors.push('Business type is required');
      return { isValid: false, errors, warnings };
    }

    // Validate unit belongs to type
    if (unitId) {
      const unit = units.find(u => u.id === unitId);
      if (unit && unit.typeId !== typeId) {
        errors.push('Selected unit does not belong to the chosen business type');
      }
    }

    // Validate cost center belongs to type
    if (costCenterId) {
      const costCenter = costCenters.find(cc => cc.id === costCenterId);
      if (costCenter && costCenter.typeId !== typeId) {
        errors.push('Selected cost center does not belong to the chosen business type');
      }
    }

    // Validate category belongs to type and cost center
    if (categoryId) {
      const category = categories.find(cat => cat.id === categoryId);
      if (category) {
        if (category.typeId !== typeId) {
          errors.push('Selected category does not belong to the chosen business type');
        }
        if (costCenterId && category.costCenterId !== costCenterId) {
          errors.push('Selected category does not belong to the chosen cost center');
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Validate dashboard comparison setup
   */
  static validateComparison(
    unitIds: string[],
    costCenterIds: string[],
    units: Unit[],
    costCenters: CostCenter[]
  ): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (unitIds.length === 0 && costCenterIds.length === 0) {
      warnings.push('No units or cost centers selected for comparison');
      return { isValid: true, errors, warnings };
    }

    // Check unit type consistency
    if (unitIds.length > 1) {
      const selectedUnits = units.filter(u => unitIds.includes(u.id));
      const unitTypes = [...new Set(selectedUnits.map(u => u.typeId))];
      
      if (unitTypes.length > 1) {
        errors.push('All selected units must belong to the same business type for meaningful comparison');
      }
    }

    // Check cost center type consistency
    if (costCenterIds.length > 1) {
      const selectedCostCenters = costCenters.filter(cc => costCenterIds.includes(cc.id));
      const ccTypes = [...new Set(selectedCostCenters.map(cc => cc.typeId))];
      
      if (ccTypes.length > 1) {
        errors.push('All selected cost centers must belong to the same business type');
      }
    }

    // Check consistency between units and cost centers
    if (unitIds.length > 0 && costCenterIds.length > 0) {
      const selectedUnits = units.filter(u => unitIds.includes(u.id));
      const selectedCostCenters = costCenters.filter(cc => costCenterIds.includes(cc.id));
      
      const unitTypes = [...new Set(selectedUnits.map(u => u.typeId))];
      const ccTypes = [...new Set(selectedCostCenters.map(cc => cc.typeId))];
      
      if (unitTypes.length === 1 && ccTypes.length === 1 && unitTypes[0] !== ccTypes[0]) {
        errors.push('Selected units and cost centers must belong to the same business type');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Get suggested filters based on current selection
   */
  static getSuggestedFilters(
    currentFilters: Partial<ExpenseDataFilters>,
    units: Unit[],
    costCenters: CostCenter[]
  ): { units: Unit[]; costCenters: CostCenter[] } {
    if (!currentFilters.typeId) {
      return { units: [], costCenters: [] };
    }

    const suggestedUnits = units.filter(u => u.typeId === currentFilters.typeId);
    const suggestedCostCenters = costCenters.filter(cc => cc.typeId === currentFilters.typeId);

    return {
      units: suggestedUnits,
      costCenters: suggestedCostCenters
    };
  }

  /**
   * Check if a comparison would be meaningful
   */
  static isMeaningfulComparison(
    unitIds: string[],
    costCenterIds: string[],
    units: Unit[],
    costCenters: CostCenter[]
  ): boolean {
    const validation = this.validateComparison(unitIds, costCenterIds, units, costCenters);
    return validation.isValid && (unitIds.length > 1 || costCenterIds.length > 1);
  }

  /**
   * Get all validation rules
   */
  static getValidationRules() {
    return HIERARCHY_VALIDATION_RULES;
  }
}
